"use client";

import { useEffect, useRef, useState } from "react";
import { Instagram, Spotify, TikTok, YouTube } from "./Icons";

// Contadores "ao vivo". As métricas grandes nascem MARGIN abaixo do número real
// e sobem em saltos aleatórios enquanto a pessoa navega, dando a sensação de
// gente entrando a todo momento. O número real é o TETO: nunca se mostra mais
// do que a rede tem (contratante confere). A cada minuto /api/stats traz o valor
// novo do coletor e o teto sobe junto.

type Stats = {
  instagram?: { ts: string; data: { followers: number; posts: number } };
  tiktok?: { ts: string; data: { followers: number; likes: number } };
  youtube?: { ts: string; data: { subscribers: number; views: number; videos: number } };
  spotify?: { ts: string; data: { monthlyListeners: number; topPlays: number } };
  facebook?: { ts: string; data: { followers: number } };
};

const fmt = new Intl.NumberFormat("pt-BR");

const MARGIN = 2000;

/** Tamanho máximo de cada salto: números maiores sobem mais rápido (plays > seguidores). */
function maxStep(value: number) {
  if (value >= 1e8) return 9;
  if (value >= 1e7) return 4;
  return 2;
}

function Ticker({ value, live }: { value: number; live: boolean }) {
  // Estado inicial determinístico: servidor e navegador renderizam o mesmo número
  const [shown, setShown] = useState(() => (live ? Math.max(0, value - MARGIN) : value));
  const [bump, setBump] = useState(0);
  const current = useRef(shown);

  // Valor real novo (coletor): o teto sobe; se o contador ficou mais de MARGIN
  // para trás, reposiciona na margem em vez de levar horas para alcançar
  useEffect(() => {
    const next = live ? Math.min(value, Math.max(current.current, value - MARGIN)) : value;
    if (next === current.current) return;
    current.current = next;
    const id = requestAnimationFrame(() => setShown(next));
    return () => cancelAnimationFrame(id);
  }, [value, live]);

  useEffect(() => {
    if (!live) return;
    let timer: ReturnType<typeof setTimeout>;
    const step = maxStep(value);
    const loop = () => {
      // intervalo irregular (0,6–2,2 s) para não parecer relógio; pausa com a aba oculta
      timer = setTimeout(() => {
        if (!document.hidden && current.current < value) {
          current.current = Math.min(value, current.current + 1 + Math.floor(Math.random() * step));
          setShown(current.current);
          setBump((b) => b + 1);
        }
        loop();
      }, 600 + Math.random() * 1600);
    };
    loop();
    return () => clearTimeout(timer);
  }, [live, value]);

  return (
    <span key={bump} className={`tabular-nums ${bump ? "tick" : ""}`}>
      {fmt.format(shown)}
    </span>
  );
}

function ago(ts: string, now: number) {
  const min = Math.max(0, Math.round((now - new Date(ts).getTime()) / 60e3));
  if (min < 1) return "agora mesmo";
  if (min < 60) return `há ${min} min`;
  return `há ${Math.round(min / 60)} h`;
}

export function LiveStats({ initial, links }: { initial: Stats; links: Record<string, string> }) {
  const [stats, setStats] = useState(initial);
  // "há X min" depende do relógio do navegador: só existe depois de montar
  // (calcular no servidor gera texto diferente e erro de hidratação #418)
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    const load = () =>
      fetch("/api/stats")
        .then((r) => r.json())
        .then(setStats)
        .catch(() => {});
    const poll = setInterval(load, 60e3);
    const tick = () => setNow(Date.now());
    const first = setTimeout(tick, 0);
    const clock = setInterval(tick, 30e3);
    return () => {
      clearTimeout(first);
      clearInterval(poll);
      clearInterval(clock);
    };
  }, []);

  // Briefing 28/09: só a métrica principal de cada rede (4 números, faixa compacta)
  const cards = [
    stats.instagram && { key: "instagram", Icon: Instagram, name: "Instagram", ts: stats.instagram.ts, value: stats.instagram.data.followers, label: "Seguidores" },
    stats.spotify && { key: "spotify", Icon: Spotify, name: "Spotify", ts: stats.spotify.ts, value: stats.spotify.data.monthlyListeners, label: "Ouvintes mensais" },
    stats.tiktok && { key: "tiktok", Icon: TikTok, name: "TikTok", ts: stats.tiktok.ts, value: stats.tiktok.data.followers, label: "Seguidores" },
    stats.youtube && { key: "youtube", Icon: YouTube, name: "YouTube", ts: stats.youtube.ts, value: stats.youtube.data.subscribers, label: "Inscritos" },
  ].filter(Boolean) as {
    key: string;
    Icon: (p: { className?: string }) => React.JSX.Element;
    name: string;
    ts: string;
    value: number;
    label: string;
  }[];

  if (cards.length === 0) return null;
  const newest = cards.map((c) => c.ts).sort().at(-1)!;

  return (
    <div>
      <p className="reveal mb-8 flex items-center justify-center gap-3 text-xs md:justify-start uppercase tracking-[0.3em] text-areia/60">
        <span className="relative flex size-2.5">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-rubi opacity-75" />
          <span className="relative inline-flex size-2.5 rounded-full bg-rubi" />
        </span>
        Ao vivo{now !== null && ` · atualizado ${ago(newest, now)}`}
      </p>
      <ul className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-champanhe/15 bg-champanhe/15 lg:grid-cols-4">
        {cards.map(({ key, Icon, name, value, label }) => (
          <li key={key} className="reveal bg-noite">
            <a
              href={links[key]}
              target="_blank"
              rel="noopener"
              data-track="social"
              data-label={name}
              className="group flex h-full flex-col items-center gap-4 p-4 text-center transition hover:bg-vinho/60 md:items-stretch md:p-7 md:text-left"
            >
              <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-champanhe md:text-sm md:tracking-[0.25em]">
                <Icon className="text-lg" /> {name}
                <span className="ml-auto hidden text-areia/30 transition group-hover:text-champanhe md:inline">↗</span>
              </span>
              <span>
                <span className="block font-display text-[clamp(1.35rem,5.5vw,2.6rem)] leading-none text-areia">
                  <Ticker value={value} live />
                </span>
                <span className="mt-2 block text-[0.7rem] uppercase tracking-[0.2em] text-champanhe/70">{label}</span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
